import { useQueryClient } from "@tanstack/react-query";
import React from "react";

import { useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";
import { type PaginatedResponse } from "@/shared/http";

import { datasetQueryKeys } from "./useDatasetsQuery";

/** Page size requested per search. Bounded so the caller renders a short list
 *  and the user types to narrow rather than scrolling. */
const SEARCH_LIMIT = 20;

/**
 * Lazy, server-side search over an org's dataset tags.
 *
 * Returns a stable function that resolves one page of matching tags. Unlike
 * {@link useDatasetTags}, nothing is fetched until it is called.
 */
export function useDatasetTagSearch(
  orgId?: string,
): (
  search: string,
  signal?: AbortSignal,
) => Promise<PaginatedResponse<string>> {
  const { currentOrganization } = useAuth();
  const { datasetService } = useDomainServices();
  const queryClient = useQueryClient();
  const canonicalOrgId = orgId ?? currentOrganization?.org_id ?? "";

  return React.useCallback(
    (search, signal) => {
      const fetchPage = (abortSignal?: AbortSignal) =>
        datasetService.getTagsForOrg(
          { search: search || undefined, limit: SEARCH_LIMIT },
          { resourceOwnerId: canonicalOrgId, abortSignal },
        );

      if (search !== "") {
        return fetchPage(signal);
      }

      return queryClient.ensureQueryData({
        queryKey: datasetQueryKeys.orgTagSearch(canonicalOrgId, ""),
        queryFn: ({ signal: querySignal }) => fetchPage(querySignal),
      });
    },
    [queryClient, datasetService, canonicalOrgId],
  );
}
